import { GTM_EVENTS, GTM_VARIABLES } from '@/constants/gtm';
import { createLogger } from '@/lib/debug';
import gtm from '@/lib/gtm';
import optimizely from '@/lib/optimizely';
import { onOptimizelyLoad } from '@/scripts/Optimizely';
import { maybeNotSet } from '@/lib/typescript-helpers';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const logger = createLogger('optimizely');

export const OptimizelyContext = createContext<OptimizelyContextType>({
  campaignIds: [],
  variationIds: [],
});

export type OptimizelyContextType = {
  campaignIds: string[];
  variationIds: string[];
};

const optimizelyReadyHandler = () => {
  logger.log('Removing white-out...');
  document.documentElement.classList.remove('hide');
};

/**
 * Core functionality for the useOptimizelyContext hooks (which have have different imp
 * implementations below for app router and pages router). These hooks provide
 * the context value for the OptimizelyContextProvider which wraps the application.
 * 
 * This context provider sets up listeners for campaign decisions and route changes to
 * keeo campaign states updates as the user navigates the application. It also handles
 * a 'white-out' functionality on initial load to prevent FOUC.
 * 
 * This context provider wrapper is required for the <OptimizelyDecision> and useOptimizelyDecision
 * hook to work. Those are also below and can be used to programatically change the application 
 * based on a campaign variation. 
 *
 * @param setupRouteChangeDetection - A function that should configure route change detection.
 * The setup function receives a callback to call when route changes occur.
 *
 * @returns An object containing the campaign and variation IDs. 
 */
const useOptimizelyCore = (
  setupRouteChangeDetection: (onRouteChange: () => void) => (() => void) | void,
): OptimizelyContextType => {
  const [campaignIds, setCampaignIds] = useState<string[]>([]);
  const [variationIds, setVariationIds] = useState<string[]>([]);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Set up route change detection
  useEffect(() => {
    const cleanup = setupRouteChangeDetection(() => {
      setInitialLoad(false);
    });

    return cleanup;
  }, [setupRouteChangeDetection]);

  /*
   * Sets a white-out on initial load to prevent FOUC, 
   * based on the global flag window.optimizelyWaitForChanges
   */
  useEffect(() => {
    if (!initialLoad) return;

    if (window.optimizelyWaitForChanges === true) {
      logger.log('optimizelyWaitForChanges is set to true, setting white-out timeout');
      timeoutRef.current = setTimeout(optimizelyReadyHandler, 5000);
    } else if (document.documentElement.classList.contains('hide')) {
      logger.log('optimizelyWaitForChanges was not set, removing white-out');
      optimizelyReadyHandler();
    }
  }, [initialLoad]);

  useEffect(() => {
    if (!window.optimizelyActionAppliedListenerAdded) {
      window.optimizelyActionAppliedListenerAdded = true;

      const handler = () => {
        optimizelyReadyHandler();

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };

      if (window.optimizelyActionAppliedCount) {
        handler();
      } else {
        window.optimizely.push({
          type: 'addListener',
          filter: {
            type: 'action',
            name: 'applied',
          },
          handler,
        });
      }
    }
  }, []);

  const addCampaignDecision = useCallback(
    (campaignId: string, variationId: string) => {
      setCampaignIds(Array.from(new Set([...campaignIds, campaignId])));
      setVariationIds(Array.from(new Set([...variationIds, variationId])));
    },
    [campaignIds, variationIds],
  );

  useEffect(() => {
    window.optimizelyCampaignDecidedListener = addCampaignDecision;
  }, [addCampaignDecision]);

  // OneTrust C0002: when the Optimizely snippet loads (after Performance consent), this runs
  useEffect(() => {
    window.__onOptimizelyLoad = onOptimizelyLoad;
    return () => {
      delete window.__onOptimizelyLoad;
    };
  }, []);

  /*
   * Gets initially available campaign and variation IDs from Optimizely state, 
   * Sets up a listener for the campaign decided event, and adds the campaign and variation IDs to the context.
   */
  useEffect(() => {
    const { campaignIds, variationIds } = optimizely.getState();

    setCampaignIds(campaignIds);
    setVariationIds(variationIds);

    if (variationIds.length) {
      gtm.event(GTM_EVENTS.OPTIMIZELY_DECISION, {
        [GTM_VARIABLES.OPTIMIZELY_VARIATION_IDS]: variationIds.join(','),
      });
    }

    if (!window.optimizelyCampaignDecidedListenerAdded) {
      window.optimizelyCampaignDecidedListenerAdded = true;

      window.optimizely.push({
        type: 'addListener',
        filter: {
          type: 'lifecycle',
          name: 'campaignDecided',
        },
        handler: (event) => {
          const campaignName = maybeNotSet(event.data.campaign.name ?? event.data.campaign.id);
          const { variationId } = event.data.decision;

          if (!variationId) {
            logger.info(`Not activating campaign '${campaignName}': no variation is set`);

            return;
          }

          logger.info(`Variation '${variationId}' of campaign '${campaignName}' activated`);

          window.optimizelyCampaignDecidedListener(event.data.campaign.id, variationId);

          const { variationIds } = optimizely.getState();

          gtm.event(GTM_EVENTS.OPTIMIZELY_DECISION, {
            [GTM_VARIABLES.OPTIMIZELY_VARIATION_IDS]: variationIds.join(','),
          });
        },
      });
    }
  }, []);

  return {
    campaignIds,
    variationIds,
  };
};

/**
 * Pages Router version - uses next/router
 */
export const useOptimizelyContext = (): OptimizelyContextType => {
  const router = useRouter();

  const setupRouteChangeDetection = useCallback(
    (onRouteChange: () => void) => {
      const routeChangeCompleteHandler = () => {
        onRouteChange();
      };

      router.events.on('routeChangeComplete', routeChangeCompleteHandler);

      return () => {
        router.events.off('routeChangeComplete', routeChangeCompleteHandler);
      };
    },
    [router],
  );

  return useOptimizelyCore(setupRouteChangeDetection);
};

/**
 * App Router version - uses next/navigation
 */
export const useAppRouterOptimizelyContext = (): OptimizelyContextType => {
  const pathname = usePathname();
  const isInitialMount = useRef(true);
  const onRouteChangeRef = useRef<(() => void) | null>(null);

  const contextValue = useOptimizelyCore((onRouteChange) => {
    // Store the callback in a ref so we can call it from useEffect
    onRouteChangeRef.current = onRouteChange;
  });

  // Watch for pathname changes and call onRouteChange (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (onRouteChangeRef.current) {
      onRouteChangeRef.current();
    }
  }, [pathname]);

  return contextValue;
};

interface OptimizelyDecisionProps extends PropsWithChildren {
  campaignId: string;
  variationId: string;
  showByDefault?: boolean;
}

/**
 * Wrapper element to show/hide content based on the campaign and variation IDs.
 * 
 * @param campaignId - The campaign ID for the experiment.
 * @param variationId - The variation ID for which the children should be shown.
 * @param showByDefault - Whether to show the content by default if the campaign or variation is not set.
 *
 */

export const OptimizelyDecision: FC<OptimizelyDecisionProps> = ({
  campaignId,
  variationId,
  showByDefault = false,
  children,
}) => {
  const { campaignIds, variationIds } = useContext(OptimizelyContext);

  if (
    (campaignIds.includes(campaignId) && variationIds.includes(variationId)) ||
    (!campaignIds.includes(campaignId) && showByDefault)
  ) {
    return children;
  }

  return null;
};

/** 
 * Hook to check if the campaign and variation IDs are set.
 * 
 * @param defaultVariation - The variation ID to use if the campaign or variation is not set.
 * @param campaignId - The campaign ID to check.
 * @param variationIds - The variation IDs to check.
 * 
 * @returns An array of booleans indicating if the campaign and variation IDs are set.
 * 
 * Example:
 * const [isDefaultVariant, isTrialVariant] = useOptimizelyDecision({
 *   campaignId: OPTIMIZELY_TESTS.demoFieldQuantityTest.campaignID,
 *   variationIds: Object.values(OPTIMIZELY_TESTS.demoFieldQuantityTest.variants),
 * });
 */

export const useOptimizelyDecision = ({
  defaultVariation,
  campaignId,
  variationIds,
}: {
  defaultVariation?: string;
  campaignId: string;
  variationIds: string[];
}) => {
  const { campaignIds: activeCampaignIds, variationIds: activeVariationIds } = useContext(OptimizelyContext);

  const decisions = useMemo(() => {
    return variationIds.map((variationId, index) => {
      return (
        (activeCampaignIds.includes(campaignId) && activeVariationIds.includes(variationId)) ||
        (!activeCampaignIds.includes(campaignId) && defaultVariation === variationId)
      );
    });
  }, [activeCampaignIds, activeVariationIds, defaultVariation]);

  return decisions;
};
